import type { CollectionEntry } from '../types/card';
import type { PullResult, GachaSession } from '../types/pack';
import { sortCollection, upsertCollectionEntry } from '../engine/collection';

export interface LocalSnapshot {
  session: GachaSession;
  sessionDate: string;
  collection: CollectionEntry[];
  lastResult?: PullResult;
}

const EMPTY_SESSION: GachaSession = {
  pullsToday: 0,
  pullsSinceLegendary: 0,
  pullsSinceEpic: 0
};

export class LocalGameStorage {
  private readonly key: string;

  constructor(adapterId: string) {
    this.key = `gachakit:${adapterId}`;
  }

  load(): LocalSnapshot {
    if (typeof localStorage === 'undefined') {
      return this.createEmptySnapshot();
    }

    const raw = localStorage.getItem(this.key);
    if (!raw) {
      return this.createEmptySnapshot();
    }

    try {
      const parsed = JSON.parse(raw) as LocalSnapshot;
      const today = localDateKey();
      if (parsed.sessionDate !== today) {
        return {
          ...parsed,
          sessionDate: today,
          session: {
            ...parsed.session,
            pullsToday: 0
          }
        };
      }

      return parsed;
    } catch {
      return this.createEmptySnapshot();
    }
  }

  save(snapshot: LocalSnapshot): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.key, JSON.stringify(snapshot));
  }

  savePull(result: PullResult): LocalSnapshot {
    const snapshot = this.load();
    const nextSnapshot: LocalSnapshot = {
      sessionDate: localDateKey(),
      session: result.session,
      collection: sortCollection(upsertCollectionEntry(snapshot.collection, result.card)),
      lastResult: result
    };

    this.save(nextSnapshot);
    return nextSnapshot;
  }

  reset(): LocalSnapshot {
    const empty = this.createEmptySnapshot();
    this.save(empty);
    return empty;
  }

  private createEmptySnapshot(): LocalSnapshot {
    return {
      session: { ...EMPTY_SESSION },
      sessionDate: localDateKey(),
      collection: []
    };
  }
}

function localDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

